import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import type { BatchSummary } from "../lib/types";

interface BatchStep {
  id: string;
  step_number: number;
  title: string;
  body: string;
  due_date: string;
  status: string;
}
interface DiaryEntry {
  id: string;
  day_number: number;
  kind: string;
  title: string;
  body: string;
  gravity_reading: number | null;
  created_at: string;
}
interface ComparedBatch {
  name: string;
  targetDays: number;
  targetAbvPercent: number | null;
  targetOriginalGravity: number | null;
  actualAbvPercent: number | null;
}
interface BatchDetail {
  steps: BatchStep[];
  diary: DiaryEntry[];
  comparedTo: ComparedBatch | null;
}

const RANGES = ["Six months", "This year", "Everything"];

export function BatchLog() {
  const [params, setParams] = useSearchParams();
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [status, setStatus] = useState<"active" | "done" | "">("active");
  const [range, setRange] = useState(RANGES[0]);
  const [openId, setOpenId] = useState<string | null>(params.get("open"));
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [gravityDraft, setGravityDraft] = useState("");

  async function load() {
    const res = await api.get<{ batches: BatchSummary[] }>(`/batches${status ? `?status=${status}` : ""}`);
    setBatches(res.batches);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    if (!openId) return setDetail(null);
    api.get<BatchDetail>(`/batches/${openId}`).then(setDetail);
  }, [openId]);

  function toggle(id: string) {
    const next = openId === id ? null : id;
    setOpenId(next);
    const p = new URLSearchParams(params);
    if (next) p.set("open", next);
    else p.delete("open");
    setParams(p, { replace: true });
  }

  async function completeStep(batchId: string, stepNumber: number, status: string) {
    await api.post(`/batches/${batchId}/steps/${stepNumber}/complete`, { status });
    await load();
    if (openId === batchId) api.get<BatchDetail>(`/batches/${batchId}`).then(setDetail);
  }

  async function pushStep(batchId: string, stepNumber: number) {
    await api.post(`/batches/${batchId}/steps/${stepNumber}/push`, { days: 2 });
    await load();
    if (openId === batchId) api.get<BatchDetail>(`/batches/${batchId}`).then(setDetail);
  }

  async function addDiaryEntry(batchId: string) {
    if (!noteDraft.trim()) return;
    await api.post(`/batches/${batchId}/diary`, { title: "Note", entryText: noteDraft.trim() });
    setNoteDraft("");
    api.get<BatchDetail>(`/batches/${batchId}`).then(setDetail);
  }

  async function addGravityReading(batchId: string) {
    const value = Number(gravityDraft);
    if (!value || value <= 0) return;
    await api.post(`/batches/${batchId}/gravity`, { value });
    setGravityDraft("");
    await load();
    api.get<BatchDetail>(`/batches/${batchId}`).then(setDetail);
  }

  const activeJars = batches.filter((b) => b.status === "active").length;
  const totalLitres = batches.reduce((s, b) => s + (b.targetYieldMl ?? 0), 0) / 1000;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h6 className="kicker">
            Batch log · {activeJars} jar{activeJars === 1 ? "" : "s"}, {totalLitres.toFixed(1)} litres
          </h6>
          <h1 className="page-title">What's on the shelf</h1>
        </div>
        <div className="flex-row">
          <div className="seg">
            {(["active", "done", ""] as const).map((s) => (
              <label key={s || "all"} className="seg-opt">
                <input type="radio" name="statusFilter" checked={status === s} onChange={() => setStatus(s)} />
                {s === "active" ? "Active" : s === "done" ? "Done" : "All"}
              </label>
            ))}
          </div>
        </div>
      </div>

      <hr className="hr" style={{ margin: "30px 0 0" }} />

      <div style={{ display: "grid", gridTemplateColumns: "220px minmax(0,1fr) 130px", gap: 22, padding: "10px 0", borderBottom: "2px solid var(--color-divider)" }} className="muted-strong">
        <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>Jar</span>
        <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>Progress</span>
        <span style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "right" }}>Next</span>
      </div>

      {batches.map((b) => (
        <div key={b.id}>
          <div
            onClick={() => toggle(b.id)}
            style={{ display: "grid", gridTemplateColumns: "220px minmax(0,1fr) 130px", gap: 22, padding: "18px 0", borderBottom: "1px solid var(--color-divider)", alignItems: "center", cursor: "pointer" }}
          >
            <div>
              <p style={{ margin: 0, fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 16, letterSpacing: "-0.012em" }}>{b.name}</p>
              <p className="muted-strong" style={{ margin: "4px 0 0", fontSize: 12 }}>
                {b.vessel} · Day {b.dayNumber}/{b.targetDays}
                {b.targetAbvPercent ? ` · ${b.targetAbvPercent.toFixed(1)}%` : ""}
              </p>
            </div>
            <div style={{ position: "relative", height: 26, background: "var(--color-neutral-200)" }}>
              <div style={{ position: "absolute", inset: 0, width: `${b.percentComplete}%`, background: b.nextStep?.isDue ? "var(--color-accent)" : "var(--color-neutral-800)" }} />
            </div>
            <div style={{ textAlign: "right" }}>
              <p className="tabular" style={{ margin: 0, fontSize: 12.5, color: b.nextStep?.isDue ? "var(--color-accent)" : undefined, fontWeight: b.nextStep?.isDue ? 800 : 400 }}>
                {b.status === "done" ? "Done, drinking" : b.nextStep ? (b.nextStep.isDue ? "Due now" : b.nextStep.dueDate) : "Resting"}
              </p>
            </div>
          </div>

          {openId === b.id && detail && (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)", gap: 56, margin: "24px 0 48px", alignItems: "start" }}>
              <section>
                <h2 style={{ margin: 0, fontSize: 22, letterSpacing: "-0.015em", borderBottom: "2px solid var(--color-divider)", paddingBottom: 12 }}>
                  {b.name} — the diary
                </h2>
                {detail.diary.map((d) => (
                  <div key={d.id} style={{ display: "grid", gridTemplateColumns: "96px minmax(0,1fr)", gap: 20, padding: "16px 0", borderBottom: "1px solid var(--color-divider)", alignItems: "baseline" }}>
                    <span className="eyebrow tabular">Day {d.day_number}</span>
                    <span>
                      <span style={{ display: "block", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 15 }}>
                        {d.title}
                        {d.kind === "gravity_reading" && d.gravity_reading != null ? ` — ${d.gravity_reading.toFixed(3)}` : ""}
                      </span>
                      <span className="muted" style={{ display: "block", marginTop: 5, fontSize: 13.5, lineHeight: 1.55 }}>
                        {d.body}
                      </span>
                    </span>
                  </div>
                ))}
                <div className="flex-row" style={{ marginTop: 16 }}>
                  <input className="input" placeholder="Add an entry…" value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} />
                  <button className="btn btn-secondary" onClick={() => addDiaryEntry(b.id)}>
                    Add
                  </button>
                </div>
              </section>

              <aside className="flex-col" style={{ gap: 28 }}>
                {detail.steps.filter((s) => s.status === "pending").slice(0, 1).map((step) => (
                  <div key={step.id} className="panel">
                    <h6 className="eyebrow" style={{ marginBottom: 10 }}>
                      Due {step.due_date}
                    </h6>
                    <p style={{ margin: 0, fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 22, lineHeight: 1.2 }}>{step.title}</p>
                    <p className="muted" style={{ margin: "8px 0 16px", fontSize: 13.5, lineHeight: 1.55 }}>
                      {step.body}
                    </p>
                    <div className="wrap-gap">
                      <button className="btn btn-primary" onClick={() => completeStep(b.id, step.step_number, "on_time")}>
                        Mark done
                      </button>
                      <button className="btn btn-secondary" onClick={() => pushStep(b.id, step.step_number)}>
                        Push 2 days
                      </button>
                      <button className="btn btn-ghost" onClick={() => completeStep(b.id, step.step_number, "skipped")}>
                        Skip
                      </button>
                    </div>
                  </div>
                ))}

                {b.targetOriginalGravity != null && (
                  <div className="panel">
                    <h6 className="eyebrow" style={{ marginBottom: 10 }}>
                      Gravity
                    </h6>
                    <p style={{ margin: 0, fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 22, lineHeight: 1.2 }}>
                      {(b.actualOriginalGravity ?? b.targetOriginalGravity).toFixed(3)} →{" "}
                      {b.actualFinalGravity != null ? b.actualFinalGravity.toFixed(3) : b.targetFinalGravity?.toFixed(3)}
                    </p>
                    <p className="muted" style={{ margin: "8px 0 16px", fontSize: 13.5 }}>
                      {b.actualAbvPercent != null
                        ? `${b.actualAbvPercent.toFixed(1)}% ABV, from logged readings.`
                        : `Target ${b.targetAbvPercent?.toFixed(1)}% — log a hydrometer reading to track it for real.`}
                    </p>
                    <div className="flex-row">
                      <input
                        className="input tabular"
                        type="number"
                        step="0.001"
                        placeholder="1.046"
                        value={gravityDraft}
                        onChange={(e) => setGravityDraft(e.target.value)}
                      />
                      <button className="btn btn-secondary" onClick={() => addGravityReading(b.id)}>
                        Log reading
                      </button>
                    </div>
                  </div>
                )}

                {detail.comparedTo && (
                  <div style={{ borderTop: "2px solid var(--color-divider)", paddingTop: 18 }}>
                    <h6 className="eyebrow" style={{ marginBottom: 12 }}>
                      This batch vs. your last
                    </h6>
                    <div className="spread" style={{ fontSize: 13.5, padding: "8px 0", borderBottom: "1px solid var(--color-divider)" }}>
                      <span>Target days</span>
                      <span className="tabular">
                        {b.targetDays} · <span className="muted-strong">was {detail.comparedTo.targetDays}</span>
                      </span>
                    </div>
                    {detail.comparedTo.targetAbvPercent != null && b.targetAbvPercent != null && (
                      <div className="spread" style={{ fontSize: 13.5, padding: "8px 0", borderBottom: "1px solid var(--color-divider)" }}>
                        <span>Target ABV</span>
                        <span className="tabular">
                          {b.targetAbvPercent.toFixed(1)}% · <span className="muted-strong">was {detail.comparedTo.targetAbvPercent.toFixed(1)}%</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div style={{ borderTop: "2px solid var(--color-divider)", paddingTop: 18 }}>
                  <h6 className="eyebrow" style={{ marginBottom: 12 }}>
                    All steps
                  </h6>
                  {detail.steps.map((s) => (
                    <div key={s.id} className="spread" style={{ fontSize: 13, padding: "8px 0", borderBottom: "1px solid var(--color-divider)" }}>
                      <span style={{ textDecoration: s.status !== "pending" ? "line-through" : "none", opacity: s.status !== "pending" ? 0.55 : 1 }}>{s.title}</span>
                      <span className="muted-strong tabular">{s.status === "pending" ? s.due_date : s.status}</span>
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          )}
        </div>
      ))}

      {batches.length === 0 && (
        <p className="muted" style={{ padding: "30px 0" }}>
          No batches here yet.
        </p>
      )}
    </main>
  );
}
